import React, { useCallback, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import Dropdown from '../Dropdown/Dropdown';
import Textarea from '../Textarea-new/Textarea';
import Input from '../Input-new/Input';
import SelectEscrowOfficer from '../SelectEscrowOfficer/SelectEscrowOfficer';
import LoanQuoteNotes from '../LoanQuoteNotes/LoanQuoteNotes';
import { lenderWithNotes } from '../../loan-quote-lender-notes';
import { isLoanOption } from '../../utils';
import Icon from '../Icon/Icon';
import trash from '../../svgs/Trash.svg';
import grip from '../../svgs/drag-indicator.svg';
import copy from '../../svgs/copy.svg';

const VerticalTableDragableCard = ({ column, headings, columnIndex, isEditable, isPrint,printTableColumns, handleChangeDropdown, handleChange, handleInputFocus, handlePrintKeyClick, handlePrintPagesDropDown, moveCard, handleKeyDown, onDuplicate, onDelete}) => {

    function renderInputElement({column, heading, isEditable, columnIndex, isPrint,printTableColumns, handleInputFocus}) {
        // Some rows only apply to the first loan quote (e.g. Approved); leave the
        // rest of the columns blank for those rows.
        if (heading.firstColumnOnly && columnIndex !== 0) {
            return null;
        }
        const id = `${heading.key}-${columnIndex}`;
        // Figures auto-filled onto a column nobody quoted on are hidden while it
        // is unticked; a column holding a quote shows them whether it prints or not.
        const hideAutoPopulateKey = ['NOI','NOI_PERCENTAGE','NOI_MULTIPLIED','PURCHASE_PRICE'].includes(heading.key) && (!column[`IS_PRINTABLE`]) && (+column['AUTOPOPULATE']) && !isLoanOption(column);
        switch (heading.format) {
            case 'dropdown':
                return <Dropdown
                    id={id}
                    theme='no-border'
                    viewClassName='vertical-table'
                    fillText={heading.dropdownType === 'loanquotes-amortization' ? ' Years' : ''}
                    optionsType={heading.dropdownType}
                    inputMode={true}
                    customValues={!!heading.customValues}
                    valueKey={id}
                    className='VerticalTable_input'
                    data={column}
                    value={column[heading.key]}
                    tooltip={heading.tooltip}
                    viewMode={!isEditable}
                    handleParentChange={handleChangeDropdown}
                    handleInputFocus = {()=>handleInputFocus(id)}
                    openOnFocus={true}
                />
                
            // Vendor picker backed by VENDORS of one VENDOR_TYPE, with Quick add.
            // Selecting a contact also writes the vendor's company into
            // heading.companyKey, so Lender fills itself in from Lender Contact.
            case 'vendorSelect':
                // The card's onKeyDown drives arrow-key navigation between rows
                // and reads an activeElementId that only Input/Dropdown/Textarea
                // ever set. Arrow keys belong to the autocomplete's own option
                // list here, so keep them from bubbling out to it.
                return <div onKeyDown={(e) => e.stopPropagation()}>
                    <SelectEscrowOfficer
                        value={column[heading.key]}
                        vendorType={heading.vendorType}
                        contactNoun={heading.contactNoun}
                        companyFilter={heading.companyKey ? column[heading.companyKey] : null}
                        disabled={!isEditable}
                        onSelect={(name, companyName) => {
                            handleChangeDropdown(name, null, null, id);
                            if (heading.companyKey) {
                                handleChangeDropdown(companyName || '', null, null, `${heading.companyKey}-${columnIndex}`);
                            }
                        }}
                    />
                </div>

            case 'textarea': {
                const lender = heading.lenderKey && isEditable && !isPrint ? lenderWithNotes(column[heading.lenderKey]) : null;
                if (lender) {
                    return <LoanQuoteNotes
                        id={id}
                        value={column[heading.key]}
                        lender={lender}
                        className='VerticalTable_input height56'
                        tooltip={heading.tooltip}
                        handleChange={handleChange}
                        handleInputFocus={() => handleInputFocus(id)}
                    />
                }
                return <Textarea
                    id={id}
                    classNameModifier="vertical-table"
                    theme='without-border'
                    className={`VerticalTable_input ${isPrint ? 'notesPrintsize' : '' } height56`}
                    name={id}
                    value={column[heading.key]}
                    viewMode={!isEditable}
                    tooltip={heading.tooltip}
                    handleParentChange={handleChange}
                    handleInputFocus = {()=>handleInputFocus(id)}
                />
            }
            case 'checkbox':
                return <div className="print-control" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "14px" }}>
                    {
                        // The only drag source on the column. Reordering used to be
                        // grab-anywhere, which meant pressing into a cell and moving
                        // even slightly dragged the quote instead of putting the
                        // caret in the field.
                        isEditable && moveCard &&
                        <span
                            ref={drag}
                            className="VerticalTable_dragHandle"
                            title="Drag to reorder this quote"
                            aria-label="Drag to reorder this quote"
                        >
                            <Icon svg={grip}/>
                        </span>
                    }
                    <input
                        type="checkbox"
                        name={`${heading.key}-${columnIndex}`}
                        key={`${heading.key}`} disabled={!isEditable}
                        value={column[heading.key]}
                        checked={column[heading.key]} 
                        onClick={e => handlePrintKeyClick(e, column, heading)}
                    />
                    {
                        isEditable ? (
                            <select className="custom_select" onChange={(e) => handlePrintPagesDropDown(+e.target.value, `PRINT_PAGE_NUMBER-${columnIndex}`)} value={column["PRINT_PAGE_NUMBER"]}>
                            <option value={1} disabled={printTableColumns.firstPagePrintColumns === 5}>1</option>
                        <option value={2} disabled={printTableColumns.secondPagePrintColumns ===5 || !printTableColumns.firstPagePrintColumns}>2</option>
                        <option value={3} disabled={printTableColumns.thirdPagePrintColumns ===5 || !printTableColumns.secondPagePrintColumns}>3</option>
                        <option value={4} disabled={printTableColumns.fourthPagePrintColumns ===5 || !printTableColumns.thirdPagePrintColumns}>4</option>
                        <option value={5} disabled={printTableColumns.fifthPagePrintColumns ===5 || !printTableColumns.fourthPagePrintColumns}>5</option>
                            </select>
                        ) : (
                            column["PRINT_PAGE_NUMBER"]
                        )
                    }
                    {
                        // Most quotes on a property differ by a row or two, so a
                        // copy lands beside this one ready for those edits.
                        isEditable && onDuplicate &&
                        <button
                            type="button"
                            className="VerticalTable_copyColumn"
                            title="Copy this loan quote"
                            aria-label="Copy this loan quote"
                            draggable={false}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                        >
                            <Icon svg={copy}/>
                        </button>
                    }
                    {
                        // Deleting a whole quote — the column, not a value — so the
                        // button sits with the other per-column controls at its top.
                        isEditable && onDelete &&
                        <button
                            type="button"
                            className="VerticalTable_deleteColumn"
                            title="Delete loan quote"
                            aria-label="Delete loan quote"
                            draggable={false}
                            // The card itself is draggable; a press on the button is
                            // a click, not the start of a drag.
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        >
                            <Icon svg={trash}/>
                        </button>
                    }
                </div>
            default:
                return <Input
                    id={id}
                    theme={`without-border ${column[heading.key] ? heading.key:''}`}
                    valueType={heading.format}
                    className='VerticalTable_input'
                    name={id}
                    value={hideAutoPopulateKey ?  ``:column[heading.key]}
                    isDollar={!hideAutoPopulateKey && heading.isDollar}
                    viewMode={!isEditable}
                    disabled={!!heading.disabled}
                    decimals={heading.decimals}
                    maxDecimals={heading.maxDecimals}
                    handleParentChange = {handleChange}
                    handleInputFocus = {()=>handleInputFocus(id)}
                    tooltip={heading.tooltip}
                    tooltipPosition='custom-down'
                />
        }
    }

    const ref = useRef(null)
    const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
        return {
        handlerId: monitor.getHandlerId(),
        }
    },
    hover(item, monitor) {
        if (!ref.current) {
        return
        }
        const dragIndex = item.index
        const hoverIndex = columnIndex

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
        return
        }

        // Time to actually perform the action
        moveCard(dragIndex, hoverIndex)

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex
    },
    })

    const [{ isDragging }, drag, preview] = useDrag({
    type: 'card',
    item: () => {
        return { index: columnIndex }
    },
    collect: (monitor) => ({
        isDragging: monitor.isDragging(),
    }),
    })

    const opacity = isDragging ? 0 : 1

    // The whole column takes the drop and is the drag image; only the grip at its
    // top (rendered with the print controls above) is the drag source. The grip
    // takes the connector as its ref rather than a ref object: it only exists once
    // the tab is editable, which is well after react-dnd's mount-time reconnect,
    // so a ref object would sit there unconnected and nothing would drag at all.
    const connectColumn = useCallback((node) => {
        ref.current = node;
        drop(node);
        preview(node);
    }, [drop, preview])
    
  return (
    <div ref={connectColumn} style={{ opacity, backgroundColor: 'white' }} data-handler-id={handlerId} onKeyDown={(e)=> handleKeyDown(e)} id={columnIndex}>
        {
            headings.map((heading, headingIndex) => (
                <div className={`VerticalTable_td ${headingIndex === 0 && 'paddingTop10 stickToTop top0'} ${headingIndex === 1 && 'stickToTop top40'} ${heading.name === 'Notes' && 'margineBottom30'}`} key={`${headingIndex}-${columnIndex}`} id={heading.key}>
                    {
                        renderInputElement({column, heading, isEditable, columnIndex, isPrint,printTableColumns, handleInputFocus})
                    }
                </div>
            ))
        }
    </div>
  )
}

export default VerticalTableDragableCard
